import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CURRENCIES,
  contentFor,
  formatPrice,
  type Locale,
  type Product,
} from "@nh/shared";
import { SelectField } from "@/shared/ui/core";
import { NumberField } from "@/features/admin/components";
import type { PackageFormValues } from "../packageForm";

export interface PackageItemRowProps {
  /** La ruta de ESTA fila en el formulario: `"items.0"`. */
  name: string;
  /** El catálogo, para el selector y para el renglón de subtotal. */
  products: readonly Product[];
  catalog: ReadonlyMap<string, Product>;
  /** `true` mientras el catálogo todavía no llegó. */
  loading: boolean;
}

/** El idioma del contenido que se enseña, derivado del de la interfaz. */
function contentLocale(language: string): Locale {
  return language.startsWith("en") ? "en" : "es";
}

/**
 * Una línea del paquete: qué producto y cuántos.
 *
 * ── El renglón de abajo ──────────────────────────────────────────────
 * No es adorno. Un paquete se arma eligiendo entre productos que se
 * parecen, y el precio unitario y los VP son justo lo que los distingue.
 * Sin ese renglón hay que abrir el catálogo en otra pestaña para saber
 * cuál de los dos frascos cuesta más — y entonces el descuento se elige
 * sin saber sobre qué se aplica.
 *
 * Va DEBAJO y no dentro del `<option>`: un `<select>` nativo con
 * "Colágeno — $1,299.50 — 40 VP" en cada opción se vuelve ilegible en
 * el móvil, que es donde el nativo tiene su ventaja.
 */
export function PackageItemRow({
  name,
  products,
  catalog,
  loading,
}: PackageItemRowProps) {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext<PackageFormValues>();
  const locale = contentLocale(i18n.language);
  const priceLocale = i18n.language.startsWith("en") ? "en-US" : "es-MX";

  const productId = useWatch({ control, name: `${name}.productId` as "items.0.productId" });
  const quantityRaw = useWatch({ control, name: `${name}.quantity` as "items.0.quantity" });

  const product = productId ? catalog.get(productId) : undefined;
  const quantity = Number(quantityRaw);
  const units = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

  const options = products.map((item) => ({
    value: item.id,
    label: contentFor(item.translations, locale).name,
  }));

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_130px]">
      {/*
        `value` explícito, y no solo lo que pone `register`.

        Un `<select>` de react-hook-form es NO controlado: el valor se
        escribe en el DOM al montar. Y aquí el catálogo llega DESPUÉS —es
        otra petición—, así que al montar la lista de `<option>` está
        vacía: el navegador no encuentra la opción `prd_1`, se queda con
        el hueco vacío, y cuando las opciones aparecen ya nadie vuelve a
        poner el valor. El resultado era un paquete guardado que al
        abrirlo enseñaba "Elige un producto" en todas sus filas, con el
        precio correcto debajo — o sea, el formulario sabía cuál era y la
        pantalla decía que no.

        Pasando `value` el control queda gobernado por el formulario, que
        es quien tiene la verdad, y el orden de llegada deja de importar.
        El `onChange` sigue siendo el de `register`.
      */}
      <SelectField
        name={`${name}.productId`}
        label={t("Producto")}
        value={productId ?? ""}
        placeholder={loading ? t("Cargando el catálogo…") : t("Elige un producto")}
        options={options}
        disabled={loading || undefined}
      />

      <NumberField
        name={`${name}.quantity`}
        label={t("Cuántos")}
        placeholder="1"
        className="text-right tabular-nums"
      />

      {/*
        `flex-wrap` y no un párrafo con `whitespace-nowrap` suelto: sin
        espacios de texto entre los tramos, un párrafo no tiene DÓNDE
        partir la línea, y a 390 px el renglón se salía del panel con los
        VP cortados por la mitad. En flex, cada tramo es un ítem y el
        salto ocurre entre ellos; el `nowrap` de dentro sigue impidiendo
        que se parta un importe.
      */}
      {product && (
        <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] leading-relaxed text-ds-ink-3 sm:col-span-2">
          {CURRENCIES.filter((currency) => product.prices[currency] !== undefined).map(
            (currency) => (
              <span key={currency} className="whitespace-nowrap">
                {formatPrice((product.prices[currency] as number) * units, currency, priceLocale)}
                {units > 1 && (
                  <span>
                    {" "}
                    ({units} × {formatPrice(product.prices[currency] as number, currency, priceLocale)})
                  </span>
                )}
              </span>
            ),
          )}
          <span className="whitespace-nowrap">
            {product.volumePoints === undefined
              ? t("Sin VP capturados")
              : t("{{points}} VP", { points: product.volumePoints * units })}
          </span>
          {/*
            ── El STOCK del subproducto (Anahí, 27-ago) ────────────────
            Es la referencia de cuántos tendrá la tienda del
            franquiciatario: de aquí sale su inventario al comprar el
            paquete. Se dice el disponible del catálogo y, si la línea
            pide más de lo que hay, se avisa en el acto.
          */}
          {product.stock === undefined ? (
            <span className="whitespace-nowrap">{t("Stock: sin control")}</span>
          ) : (
            <span
              className={
                units > product.stock
                  ? "whitespace-nowrap font-medium text-ds-bad-t"
                  : "whitespace-nowrap"
              }
            >
              {units > product.stock
                ? t("Stock: {{stock}} — este paquete pide {{units}}", {
                    stock: product.stock,
                    units,
                  })
                : t("Stock: {{stock}}", { stock: product.stock })}
            </span>
          )}
        </p>
      )}

      {/*
        Un producto elegido que el catálogo no conoce no se puede
        silenciar: su precio no entra en la suma, y sin este renglón el
        total de arriba parecería completo.
      */}
      {productId && !product && !loading && (
        <p className="text-[12px] font-medium leading-relaxed text-ds-bad-t sm:col-span-2">
          {t("Este producto ya no está en el catálogo: quítalo o elige otro.")}
        </p>
      )}
    </div>
  );
}
